/**
 * StepIndicator — Recessed LCD step display bar
 *
 * Shows current step label with square progress dots.
 * Only shows the 5 booking steps (date, time, players, summary, payment).
 * Login, verify, and confirmation are outside the progress bar.
 */

import { BOOKING_STEPS, STEP_LABELS, type BookingStepV2 } from '../hooks/useBookingState'

interface StepIndicatorProps {
  currentStep: BookingStepV2
  bookingStepIndex: number
  isBookingStep: boolean
}

export function StepIndicator({ currentStep, bookingStepIndex, isBookingStep }: StepIndicatorProps) {
  if (!isBookingStep) {
    return (
      <div className="bw2-step-display" role="status" aria-label={STEP_LABELS[currentStep]}>
        <span className="bw2-step-display__label">
          {STEP_LABELS[currentStep]}
        </span>
      </div>
    )
  }

  const stepName = STEP_LABELS[currentStep]
  const valueText = `Step ${bookingStepIndex + 1} of ${BOOKING_STEPS.length}: ${stepName}`

  return (
    <div
      className="bw2-step-display"
      role="progressbar"
      aria-valuenow={bookingStepIndex + 1}
      aria-valuemin={1}
      aria-valuemax={BOOKING_STEPS.length}
      aria-valuetext={valueText}
      aria-label={`Booking progress: ${valueText}`}
    >
      <span className="bw2-step-display__label">
        {stepName}
      </span>

      <div className="bw2-step-display__progress" aria-hidden="true">
        {BOOKING_STEPS.map((step, index) => {
          const isActive = index === bookingStepIndex
          const isCompleted = index < bookingStepIndex

          return (
            <div
              key={step}
              className={[
                'bw2-step-display__dot',
                isActive && 'bw2-step-display__dot--active',
                isCompleted && 'bw2-step-display__dot--completed',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          )
        })}
        <span className="bw2-step-display__counter">
          {bookingStepIndex + 1}/{BOOKING_STEPS.length}
        </span>
      </div>
    </div>
  )
}
